import React, { Context, useContext, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useHistory, useLocation } from 'react-router';
import {
  identifyUser,
  getUser,
  getUserAttributes,
  preLoginUrls,
  pathname,
  allowedDirectUrls,
  forwardUrlParams,
  GOOGLE_SIGNUP_ERROR,
} from '../utils';

const SUPER_ADMIN_ROLE = 'super_admins';

export interface IAuthContext {
  email: string;
  firstName: string;
  lastName: string;
  token: string;
  signedIn: boolean;
  signIn: (...args) => any;
  signOut: (...args) => any;
  user: any;
  setUser: (...args) => any;
  isAdminUser: boolean;
}

export const AuthContext: Context<IAuthContext> = React.createContext({
  email: null,
  firstName: null,
  lastName: null,
  token: null,
  signedIn: null,
  signIn: (...args) => console.warn({ signIn: { args } }),
  signOut: (...args) => console.warn({ signOut: { args } }),
  user: null,
  setUser: () => {},
  isAdminUser: null,
});

export default function AuthContextProvider({ children }) {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState({
    email: null,
    firstName: null,
    lastName: null,
    token: null,
    signedIn: null,
  });
  const [user, setUser] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  async function setUserState() {
    try {
      const user = await getUser();
      setUser(user);
    } catch (e) {}
  }

  async function setAdminUser() {
    if (!state?.signedIn) {
      setIsAdminUser(false);
    } else {
      const userAttributes = await getUserAttributes();
      const userGroups = userAttributes['cognito:groups'] || [];
      const isAdmin = userGroups.includes(SUPER_ADMIN_ROLE);
      setIsAdminUser(isAdmin);
    }
  }

  async function signIn(email?: string) {
    const user = await getUserAttributes();
    if (!email) {
      email = user.email;
    }
    const firstName = user['custom:first_name'];
    const lastName = user['custom:last_name'];
    setState({ ...state, signedIn: true, email, firstName, lastName });
  }

  async function signOut() {
    await Auth.signOut();
    setState({
      email: null,
      token: null,
      signedIn: false,
      firstName: null,
      lastName: null,
    });
  }

  function redirectToLogin() {
    history.push(pathname.preLogin.login.url, {
      search: forwardUrlParams(),
      prevPath: allowedDirectUrls.includes(location.pathname)
        ? location.pathname
        : null,
    });
  }

  useEffect(() => {
    getUserAttributes().then((userAttributes) => {
      const email = userAttributes.email;
      email ? signIn(email) : signOut();
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdminUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.signedIn]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error_description');
    if (
      !error?.includes(GOOGLE_SIGNUP_ERROR) &&
      state.signedIn != null &&
      !state.signedIn &&
      !preLoginUrls.includes(location.pathname)
    ) {
      redirectToLogin();
    }
    // eslint-disable-next-line
  }, [state.signedIn, history, location.pathname]);

  useEffect(() => {
    setUserState();
    if (state.signedIn) {
      identifyUser();
    }
    // eslint-disable-next-line
  }, [state.signedIn]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signOut,
        user,
        setUser,
        isAdminUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
export const useAuthContext = (): IAuthContext =>
  useContext(AuthContext as any);
